import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  CircularProgress,
  Typography,
  Box
} from '@mui/material';
import { Close as CloseIcon, ZoomIn as ZoomInIcon } from '@mui/icons-material';
import { axios, baseUrl } from 'Api';
import { AppContext, AuthContext } from 'Context';
import AlterImage from 'Assets/no-image-available.png';
import styles from './AlbumPhotosModal.module.css';

const AlbumPhotosModal = ({ open, onClose, album }) => {
  const { setAppSnackbar } = useContext(AppContext);
  const { getToken } = useContext(AuthContext);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [albumData, setAlbumData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch album details with photos using axios
  const fetchAlbumPhotos = async (content_id) => {
    if (!content_id) return;

    setIsLoading(true);
    const apiUrl = `${baseUrl}/getMyAlbumDetail`;
    const requestData = {
      album_id: content_id,
      token: getToken(),
    };

    try {
      const response = await axios.post(apiUrl, requestData);
      console.log('Album photos response:', response.data);
      setAlbumData(response.data?.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching album photos:', error);
      setIsLoading(false);
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Failed to load album photos'
      });
    }
  };

  // Trigger API call when modal opens with album
  useEffect(() => {
    if (open && album?.content_id) {
      fetchAlbumPhotos(album.content_id);
    }
  }, [open, album?.content_id]);

  const photos = albumData?.[0]?.photos || albumData?.photos || album?.photos || [];

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
    setPhotoViewerOpen(true);
  };

  const handleClosePhotoViewer = () => {
    setPhotoViewerOpen(false);
    setSelectedPhoto(null);
  };

  const handleClose = () => {
    onClose();
    setSelectedPhoto(null);
    setPhotoViewerOpen(false);
  };

  if (!album) return null;
    console.log('Fetching album photos for:', photos);
  return (
    <>
      {/* Main Album Photos Modal */}
      <Dialog
        open={open && !photoViewerOpen}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        className={styles.modalContainer}
      >
        <DialogTitle className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.albumInfo}>
              <Typography variant="h5" className={styles.albumTitle}>
                {album.title || 'Untitled Album'}
              </Typography>
              <Typography variant="body2" className={styles.photoCount}>
                {photos.length}
{' '}
{photos.length === 1 ? 'photo' : 'photos'}
              </Typography>
            </div>
            <IconButton
              onClick={handleClose}
              className={styles.closeButton}
              aria-label="close"
              size="large"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className={styles.modalContent}>
          {isLoading ? (
            <Box className={styles.loadingContainer}>
              <CircularProgress size={60} />
              <Typography variant="body1" className={styles.loadingText}>
                Loading photos...
              </Typography>
            </Box>
          ) : photos.length === 0 ? (
            <Box className={styles.emptyContainer}>
              <img
                src={AlterImage}
                alt="No photos"
                className={styles.emptyImage}
              />
              <Typography variant="h6" className={styles.emptyTitle}>
                No Photos Found
              </Typography>
              <Typography variant="body2" className={styles.emptyDescription}>
                No Photos
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2} className={styles.photosGrid}>
              {photos?.map((photo, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={photo.id || index}>
                  <div
                    className={styles.photoItem}
                    // onClick={() => handlePhotoClick(photo)}
                    role="button"
                    tabIndex={0}
                    // onKeyDown={(e) => {
                    //   if (e.key === 'Enter' || e.key === ' ') {
                    //     handlePhotoClick(photo);
                    //   }
                    // }}
                  >
                    <img
                      src={photo?.imagekit_url || photo?.photo_file || AlterImage}
                      alt={photo.title || `Photo ${index + 1}`}
                      className={styles.photoImage}
                      onError={(e) => {
                        e.target.src = AlterImage;
                      }}
                    />
                  </div>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
      {/* Photo Viewer Modal */}
      <Dialog
        open={photoViewerOpen}
        onClose={handleClosePhotoViewer}
        maxWidth={false}
        fullScreen
        className={styles.photoViewerModal}
      >
        <DialogContent className={styles.photoViewerContent}>
          <IconButton
            onClick={handleClosePhotoViewer}
            className={styles.photoViewerCloseButton}
            aria-label="close photo viewer"
            size="large"
          >
            <CloseIcon />
          </IconButton>

          {selectedPhoto && (
            <div className={styles.photoViewerContainer}>
              <img
                src={selectedPhoto.imagekit_url || selectedPhoto.photo_file || AlterImage}
                alt={selectedPhoto.title || 'Photo'}
                className={styles.fullSizePhoto}
                onError={(e) => {
                  e.target.src = AlterImage;
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AlbumPhotosModal;
